<template>
  <div class="resource-list-filter">
    <b-row>
      <b-col v-for="filter in filters" :key="`col-${filter.name}-${filter.type}`" md="3">
        <!-- Text Field -->
        <b-form-group
          v-if="filter.type === 'text' || filter.type === 'number'"
          :label="filter.label"
          :label-for="filter.id || filter.name"
        >
          <b-form-input
            :id="filter.id || filter.name"
            :name="filter.name"
            :type="filter.type"
            v-model="localValues[filter.name]"
            :placeholder="filter.placeholder || filter.label"
            @input="emitUpdate"
          />
        </b-form-group>

        <!-- Date Field -->
        <b-form-group
          v-else-if="filter.type === 'date'"
          :label="filter.label"
          :label-for="filter.id || filter.name"
        >
          <flat-pickr
            :id="filter.id || filter.name"
            :name="filter.name"
            v-model="localValues[filter.name]"
            :config="dateConfig"
            class="form-control"
            placeholder="YYYY-MM-DD"
            @input="emitUpdate"
          />
        </b-form-group>

        <!-- Select Field -->
        <b-form-group
          v-else-if="filter.type === 'select'"
          :label="filter.label"
          :label-for="filter.id || filter.name"
        >
          <v-select
            :id="filter.id || filter.name"
            v-model="localValues[filter.name]"
            :options="filter.options"
            :reduce="(option) => option.value || option.id"
            label="label"
            :clearable="true"
            :placeholder="`Select ${filter.label}`"
            @input="emitUpdate"
          />
        </b-form-group>

        <!-- Resource Field -->
        <b-form-group
          v-else-if="filter.type === 'resource'"
          :label="filter.label"
          :label-for="filter.id || filter.name"
        >
          <resource-dropdown
            :resource="filter.resource"
            :id="filter.id || filter.name"
            v-model="localValues[filter.name]"
            :itemsKey="filter.resource.itemsKey"
            :labelKey="filter.resource.labelKey"
            :clearable="true"
            @input="emitUpdate"
          />
        </b-form-group>

        <!-- Checkbox Field -->
        <b-form-group
          v-else-if="filter.type === 'checkbox'"
          :label-for="filter.id || filter.name"
          class="my-2"
        >
          <b-form-checkbox
            :id="filter.id || filter.name"
            :name="filter.name"
            v-model="localValues[filter.name]"
            @input="emitUpdate"
          >
            {{ filter.label }}
          </b-form-checkbox>
        </b-form-group>
      </b-col>

      <slot name="customFilters" />
    </b-row>

    <b-row v-if="showClearButton">
      <b-col class="d-flex justify-content-end mt-1">
        <b-button size="sm" variant="outline-secondary" @click="clearFilters">
          Clear Filters
        </b-button>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import ResourceDropdown from '@/components/common/ResourceDropdown.vue';
import { BButton, BCol, BFormCheckbox, BFormGroup, BFormInput, BRow } from 'bootstrap-vue';
import flatPickr from 'vue-flatpickr-component';
import vSelect from 'vue-select';

export default {
  name: 'ResourceListFilter',

  components: {
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BRow,
    BCol,
    BButton,
    flatPickr,
    vSelect,
    ResourceDropdown,
  },

  props: {
    // Array of filter configurations
    fields: {
      type: Array,
      required: true,
    },
    // Value for v-model binding
    value: {
      type: Object,
      default: () => ({}),
    },
    // Show clear filters button
    showClearButton: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      filters: [],
      localValues: {},
      dateConfig: {
        dateFormat: 'Y-m-d',
        altInput: true,
        altFormat: 'F j, Y',
        locale: {
          firstDayOfWeek: 1,
        },
      },
    };
  },

  created() {
    // Initialize the component
    this.filters = this.fields;
    this.initializeLocalValues();
  },

  watch: {
    // Watch for changes in the value prop
    value: {
      handler(val) {
        if (val) {
          const validFieldNames = new Set(this.filters.map((filter) => filter.name));
          Object.keys(val).forEach((key) => {
            if (validFieldNames.has(key)) {
              this.$set(this.localValues, key, val[key]);
            }
          });
        }
      },
      immediate: true,
      deep: true,
    },
    // Watch for changes in fields prop
    fields: {
      handler(val) {
        this.filters = val;
        this.initializeLocalValues();
      },
      deep: true,
    },
  },

  methods: {
    // Initialize local values with defaults from filter configs
    initializeLocalValues() {
      this.filters.forEach((filter) => {
        // Initialize with value from prop or default value for filter type
        const defaultValue = this.getDefaultValueForType(filter.type);
        this.$set(
          this.localValues,
          filter.name,
          this.value[filter.name] !== undefined
            ? this.value[filter.name]
            : filter.defaultValue !== undefined
              ? filter.defaultValue
              : defaultValue
        );
      });
    },

    // Get default empty value based on filter type
    getDefaultValueForType(type) {
      switch (type) {
        case 'checkbox':
          return false;
        case 'select':
        case 'resource':
          return null;
        case 'date':
          return '';
        default:
          return '';
      }
    },

    // Emit updated values
    emitUpdate() {
      this.$emit('input', { ...this.localValues });
    },

    // Clear all filters
    clearFilters() {
      this.filters.forEach((filter) => {
        this.$set(this.localValues, filter.name, this.getDefaultValueForType(filter.type));
      });
      this.emitUpdate();
    },
  },
};
</script>

<style scoped>
.resource-list-filter {
  margin-bottom: 1rem;
}
</style>
